import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, Trash2, X, Plus, Ticket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall, formatCents, dollarsToCents, type DayPass } from '@/lib/api';

export default function DayPasses() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<DayPass[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState<'today' | 'all'>('today');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const query: Record<string, string | number> = { page, per_page: 50 };
      if (filter === 'today') query.date = new Date().toISOString().slice(0, 10);
      const r = await apiCall<{ items: DayPass[] } & PageMeta>('/v1/day_passes', {
        bearerToken: token, query,
      });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally { setLoading(false); }
  }, [getToken, filter, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  async function doDelete(id: string) {
    const token = await getToken();
    await apiCall(`/v1/day_passes/${id}`, { method: 'DELETE', bearerToken: token });
    setItems((prev) => prev.filter((x) => x.id !== id));
    setPendingDelete(null);
  }

  const total = items.reduce((s, x) => s + x.price_cents, 0);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Day passes</h1>
          <p className="text-sm text-muted-foreground">
            {items.length} pass{items.length === 1 ? '' : 'es'} {filter === 'today' ? 'today' : 'shown'} · {formatCents(total)} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {(['today', 'all'] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
                  (filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent')}>
                {f === 'today' ? 'Today' : 'All'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add day pass
          </Button>
        </div>
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {!err && !loading && items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Ticket className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No day passes {filter === 'today' ? 'today yet' : 'recorded'}.
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 font-medium">Visitor</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">Price</th>
                  <th className="px-3 py-2 font-medium">Method</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const isDel = pendingDelete === d.id;
                  return (
                    <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2">
                        <div className="font-medium">{d.visitor_name}</div>
                        {(d.visitor_email || d.visitor_phone) && (
                          <div className="text-xs text-muted-foreground">
                            {[d.visitor_email, d.visitor_phone].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{d.valid_for_date}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCents(d.price_cents)}</td>
                      <td className="px-3 py-2"><Badge variant="outline">{d.method}</Badge></td>
                      <td className="px-3 py-2 text-right">
                        {isDel ? (
                          <div className="inline-flex items-center gap-1">
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void doDelete(d.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPendingDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => setPendingDelete(d.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="day passes" />

      {showCreate && (
        <AddDayPassModal
          onClose={() => setShowCreate(false)}
          onCreated={(dp) => { setItems((prev) => [dp, ...prev]); setShowCreate(false); }}
        />
      )}
    </div>
  );
}

function AddDayPassModal({ onClose, onCreated }: { onClose: () => void; onCreated: (dp: DayPass) => void }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [priceStr, setPriceStr] = useState('15');
  const [method, setMethod] = useState<DayPass['method']>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      const dp = await apiCall<DayPass>('/v1/day_passes', {
        method: 'POST',
        bearerToken: token,
        body: {
          visitor_name: name.trim(),
          visitor_email: email.trim() || null,
          visitor_phone: phone.trim() || null,
          price_cents: dollarsToCents(priceStr),
          method,
        },
      });
      onCreated(dp);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed');
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">Add day pass</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Visitor name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Price (USD)</label>
              <input inputMode="decimal" value={priceStr} onChange={(e) => setPriceStr(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value as DayPass['method'])}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="cash">Cash</option><option value="card">Card</option><option value="transfer">Transfer</option><option value="comp">Comp</option><option value="other">Other</option>
              </select>
            </div>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
